import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
// SkinSelector is passed as a prop, no need to import
import './GaragePreview.css';
import AssetManager from './game/managers/AssetManager';
import { applySkinToObject, getTextureForSkin } from './game/utils/skin';

const GaragePreview = ({ selectedSkin, username, players = [], onContinue, onSelectSkin, onLeave, onReadyToggle, isReady, SkinSelector: SkinSelectorProp }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const myTankRef = useRef(null);
  const otherTanksRef = useRef({}); // { [playerId]: { object, textMesh } }
  const getIsMobileViewport = () =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false;
  const getIsLandscapeViewport = () =>
    typeof window !== 'undefined' ? window.matchMedia('(orientation: landscape)').matches : true;
  const [isMobile, setIsMobile] = useState(getIsMobileViewport);
  const [isLandscape, setIsLandscape] = useState(() => getIsLandscapeViewport() || !getIsMobileViewport());
  const [panelOpen, setPanelOpen] = useState(() => !getIsMobileViewport());

  // Calculate ready players count (only for custom rooms)
  const readyPlayersCount = players.filter(p => p.isReady).length;
  const totalPlayersCount = players.length;
  const isCustomRoom = onReadyToggle && isReady !== undefined;

  // Offset nad tankem (v lokálních jednotkách modelu, tank má scale 0.01 => 250 = 2.5 m)
  // Ale protože tank má scale 0.01, musíme offset upravit

  // Vytvoří 3D text pro jméno.
  const createNameText = (name, font, isReady = false) => {
    const safeName = name || "Anonymous";
    const textGeo = new TextGeometry(safeName, {
      font,
      size: 1,   // V lokálních souřadnicích – po aplikaci scale 0.01 to bude 0.75 m.
      height: 5,
      curveSegments: 12,
      bevelEnabled: false,
      weight: 1,
      depth: 0.5,
    });
    // Centrum geometrie
    textGeo.computeBoundingBox();
    textGeo.center();
    const textColor = isReady ? 0x00ff00 : 0xffffff; // Green for ready, white for not ready
    const textMat = new THREE.MeshStandardMaterial({ color: textColor });
    const textMesh = new THREE.Mesh(textGeo, textMat);
    // Inicializujeme pozici; bude aktualizována v animační smyčce.
    textMesh.position.set(0, 3.5, 0);
    // Pokud chcete, můžete zvýšit renderOrder, aby se text zobrazoval navrch.
    textMesh.renderOrder = 1;
    return textMesh;
  };
  // Ensure FBX model and skin textures are available in AssetManager
  const ensureAssetsLoaded = async () => {
    try { AssetManager.getModel('tankModel'); } catch (e) { await AssetManager.loadFBXModel('tankModel', '/assets/models/T90/T90_1.fbx'); }
    const map = {
      T90A: 'textureA',
      T90B: 'textureB',
      T90C: 'textureC',
      T90D: 'textureD',
      T90E: 'textureE',
      T90F: 'textureF',
      T90G: 'textureG',
    };
    await Promise.all(Object.entries(map).map(async ([skin, texKey]) => {
      try { AssetManager.getTexture(texKey); } catch (e) { await AssetManager.loadTexture(texKey, '/assets/models/T90/' + skin + '.png'); }
    }));
  };

  // Vytvoří tank a k němu 3D text.
  const createTank = async (skin, name, isReady = false) => {
    await ensureAssetsLoaded();
    const model = AssetManager.getModel('tankModel');
    // Ensure unique materials per instance so skin changes don't affect others
    model.traverse(child => {
      if (child.isMesh && child.material) {
        child.material = child.material.clone();
        child.castShadow = true;
      }
    });
    const tex = getTextureForSkin(skin);
    if (tex) {
      applySkinToObject(model, tex);
    } else {
      model.traverse(child => {
        if (child.isMesh) {
          child.material.color.setHex(0x808080);
          child.material.needsUpdate = true;
          child.castShadow = true;
        }
      });
    }

    // Zmenšení modelu
    model.scale.set(0.01, 0.01, 0.01);
    model.position.set(0, 0.6, 0);

    // Načteme font a vytvoříme textMesh.
    const fontLoader = new FontLoader();
    return new Promise((resolve) => {
      fontLoader.load(
        process.env.PUBLIC_URL + '/assets/fonts/helvetiker_regular.typeface.json',
        (font) => {
          const textMesh = createNameText(name, font, isReady);
          // V tuto chvíli textMesh **nepřidáváme** jako child modelu – bude přidán samostatně.
          resolve({ object: model, textMesh, _currentSkin: skin, _isReady: isReady });
        }
      );
    });
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    // Initialise state synchronně
    handleChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    const orientationQuery = window.matchMedia('(orientation: landscape)');
    const handleOrientation = (event) => {
      setIsLandscape(event.matches);
    };

    handleOrientation(orientationQuery);

    if (orientationQuery.addEventListener) {
      orientationQuery.addEventListener('change', handleOrientation);
    } else {
      orientationQuery.addListener(handleOrientation);
    }

    return () => {
      if (orientationQuery.removeEventListener) {
        orientationQuery.removeEventListener('change', handleOrientation);
      } else {
        orientationQuery.removeListener(handleOrientation);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setPanelOpen(isLandscape);
    } else {
      setPanelOpen(true);
    }
  }, [isMobile, isLandscape]);

  // Inicializace scény, kamery, rendereru, světel, podlahy, podstavce a vytvoření mého tanku.
  useEffect(() => {
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.overflow = 'hidden';
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Scéna a kamera
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 3, 6);
    cameraRef.current = camera;

    // OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.target.set(0, 0.6, 0);
    controls.update();
    controlsRef.current = controls;

    // Skybox
    const cubeLoader = new THREE.CubeTextureLoader();
    scene.background = cubeLoader.load([
      'assets/cubemaps/ala_world/posx.jpg',
      'assets/cubemaps/ala_world/negx.jpg',
      'assets/cubemaps/ala_world/posy.jpg',
      'assets/cubemaps/ala_world/negy.jpg',
      'assets/cubemaps/ala_world/posz.jpg',
      'assets/cubemaps/ala_world/negz.jpg',
    ]);
    // scene.background = cubeLoader.load([
    //   'assets/cubemaps/garage/posx.jpg',
    //   'assets/cubemaps/garage/negx.jpg',SF
    //   'assets/cubemaps/garage/posy.jpg',
    //   'assets/cubemaps/garage/negy.jpg',
    //   'assets/cubemaps/garage/posz.jpg',
    //   'assets/cubemaps/garage/negz.jpg',
    // ]);

    // Světla
    scene.add(new THREE.AmbientLight(0x404040, 1));
    scene.add(new THREE.HemisphereLight(0xffffbb, 0x080820, 0.5));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(5, 10, 7.5);
    dirLight.castShadow = true;
    scene.add(dirLight);
    const spotLight = new THREE.SpotLight(0xffffff, 0.8);
    spotLight.position.set(-10, 15, 10);
    spotLight.angle = Math.PI / 6;
    spotLight.penumbra = 0.2;
    spotLight.castShadow = true;
    scene.add(spotLight);

    // Podlaha
    const textureLoader = new THREE.TextureLoader();
    const floorTex = textureLoader.load('assets/textures/grassyStoneTexture.jpg');
    floorTex.wrapS = THREE.RepeatWrapping;
    floorTex.wrapT = THREE.RepeatWrapping;
    floorTex.repeat.set(15, 15);
    const floorMat = new THREE.MeshStandardMaterial({
      map: floorTex,
      side: THREE.DoubleSide,
      roughness: 0.8,
      metalness: 0.2,
    });
    const floorMesh = new THREE.Mesh(new THREE.PlaneGeometry(50, 50), floorMat);
    floorMesh.rotation.x = -Math.PI / 2;
    floorMesh.position.y = -0.5;
    floorMesh.receiveShadow = true;
    scene.add(floorMesh);

    // Podstavec
    const standTex = textureLoader.load('assets/textures/rustyMetalTexture.jpg');
    const standMat = new THREE.MeshStandardMaterial({
      map: standTex,
      side: THREE.DoubleSide,
      bumpMap: standTex,
      bumpScale: 0.05,
    });
    const standGeom = new THREE.CylinderGeometry(5, 5, 1, 256);
    const standMesh = new THREE.Mesh(standGeom, standMat);
    standMesh.position.y = -0.95;
    standMesh.receiveShadow = true;
    scene.add(standMesh);

    // Vytvoříme můj tank
    const myTankReady = isCustomRoom ? isReady : false;
    console.log(`[${new Date().toISOString()}] Creating current user tank with isReady:`, myTankReady);
    createTank(selectedSkin, username, myTankReady).then((tankData) => {
      myTankRef.current = tankData;
      // Mark as current user's tank
      tankData.object.userData.isCurrentUser = true;
      tankData.textMesh.userData.isCurrentUser = true;
      console.log(`[${new Date().toISOString()}] Current user tank created, isReady:`, isReady);
      console.log(`[${new Date().toISOString()}] Tank object userData:`, tankData.object.userData);
      console.log(`[${new Date().toISOString()}] Text mesh userData:`, tankData.textMesh.userData);
      console.log(`[${new Date().toISOString()}] Text mesh material color:`, tankData.textMesh.material.color.getHexString());
      // Umístíme můj tank do středu
      tankData.object.position.set(0, 0.6, 0);
      scene.add(tankData.object);
      // Přidáme text samostatně do scény
      scene.add(tankData.textMesh);
      controls.target.copy(tankData.object.position);
    });

    // Resize handler
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;

      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();

      renderer.setSize(newWidth, newHeight);
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Animace
    const clock = new THREE.Clock();
    let rafId;
    const animate = () => {
      const dt = clock.getDelta();

      // Rotace mého tanku
      if (myTankRef.current) {
        myTankRef.current.object.rotation.y += dt * Math.PI / 8;
      }

      // Rozmístění ostatních tanků je nyní řešeno v useEffect po vytvoření tanků

      // Aktualizace pozice a orientace textu, aby směřoval ke kameře
      // Musíme to udělat po positionOtherTanks(), aby tanky měly správné pozice
      if (myTankRef.current && myTankRef.current.textMesh) {
        // Pro můj tank - text nad středem
        myTankRef.current.textMesh.position.set(0, 3.1, 0); // 0.6 + 2.5 = 3.1
        myTankRef.current.textMesh.lookAt(camera.position);
      }

      // Aktualizace textu pro ostatní tanky
      Object.values(otherTanksRef.current).forEach(tankData => {
        if (tankData.object && tankData.textMesh) {
          // Text nad tankem - kopírujeme X a Z pozici tanku, Y je o 2.5 vyšší
          const tankPos = tankData.object.position;
          tankData.textMesh.position.set(tankPos.x, tankPos.y + 2.5, tankPos.z);
          tankData.textMesh.lookAt(camera.position);
        }
      });

      controls.update();
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      container.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  // Aktualizace textury mého tanku při změně skinu
  useEffect(() => {
    if (!myTankRef.current) return;
    const tex = getTextureForSkin(selectedSkin);
    if (tex && myTankRef.current) {
      applySkinToObject(myTankRef.current.object, tex);
    }
  }, [selectedSkin]);

  // Vytváření / aktualizace tanků pro ostatní hráče podle players
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;
    const dict = otherTanksRef.current;

    // Filter out the current user from the players list to avoid duplicates
    const otherPlayers = players.filter(p => p.name !== username);

    console.log(`[${new Date().toISOString()}] Processing players:`, otherPlayers.map(p => ({ id: p.id, name: p.name, color: p.color, isReady: p.isReady })));

    // Create a stable key for comparison to prevent unnecessary re-runs
    const playersKey = otherPlayers.map(p => p.id).sort().join(',');



    // Check if we already processed this exact set of players
    if (dict._lastPlayersKey === playersKey) {
      console.log(`[${new Date().toISOString()}] Same players, checking for updates...`);

      // Even if same players, check for skin and ready updates
      otherPlayers.forEach(p => {
        if (dict[p.id]) {
          // Check skin updates
          if (dict[p.id]._currentSkin !== p.color) {
            const newTex = getTextureForSkin(p.color);
            if (newTex) {
              applySkinToObject(dict[p.id].object, newTex);
              dict[p.id]._currentSkin = p.color;
            } else {
              dict[p.id].object.traverse(child => {
                if (child.isMesh && child.material) {
                  child.material.color.setHex(0x808080);
                  child.material.needsUpdate = true;
                }
              });
            }
          }

          // Check ready status updates
          if (dict[p.id]._isReady !== p.isReady) {
            console.log(`[${new Date().toISOString()}] Updating ready status for player:`, p.name, 'from', dict[p.id]._isReady, 'to', p.isReady);
            console.log(`[${new Date().toISOString()}] Player data for ready update:`, p);
            const newColor = p.isReady ? 0x00ff00 : 0xffffff;
            console.log(`[${new Date().toISOString()}] Setting text color to:`, newColor === 0x00ff00 ? 'GREEN' : 'WHITE');
            dict[p.id].textMesh.material.color.setHex(newColor);
            dict[p.id]._isReady = p.isReady;
          }
        }
      });
      return;
    }

    // Store the current players key
    dict._lastPlayersKey = playersKey;

    // Odstranit tanky, které už nejsou v seznamu players
    const playerIds = otherPlayers.map(p => p.id);
    Object.keys(dict).forEach(id => {
      if (id !== '_lastPlayersKey' && !playerIds.includes(id)) {
        console.log(`[${new Date().toISOString()}] Removing tank for player ID:`, id);
        scene.remove(dict[id].object);
        scene.remove(dict[id].textMesh);
        delete dict[id];
      }
    });

    // Create all tanks first, then position them
    const createTanksPromise = Promise.all(
      otherPlayers.map(async (p) => {
        if (!dict[p.id]) {
          console.log(`[${new Date().toISOString()}] Creating new tank for player:`, p.name, 'ID:', p.id);
          // Use the correct property names from backend
          const playerName = p.name || 'Anonymous';
          const playerSkin = p.color || 'T90A';
          const playerReady = p.isReady || false;
          const tankData = await createTank(playerSkin, playerName, playerReady);
          dict[p.id] = tankData;
          scene.add(tankData.object);
          scene.add(tankData.textMesh);
          console.log(`[${new Date().toISOString()}] Added tank for player:`, p.name);
          return tankData;
        } else {
          // Update existing tank's skin if it changed
          const currentPlayer = otherPlayers.find(player => player.id === p.id);
          if (currentPlayer) {
            // Update skin if changed
            if (dict[p.id]._currentSkin !== currentPlayer.color) {
              const newTex = getTextureForSkin(currentPlayer.color);
              if (newTex) {
                applySkinToObject(dict[p.id].object, newTex);
                dict[p.id]._currentSkin = currentPlayer.color;
              } else {
                dict[p.id].object.traverse(child => {
                  if (child.isMesh && child.material) {
                    child.material.color.setHex(0x808080);
                    child.material.needsUpdate = true;
                  }
                });
              }
            }

            // Update ready status if changed
            if (dict[p.id]._isReady !== currentPlayer.isReady) {
              console.log(`[${new Date().toISOString()}] Updating ready status for player:`, p.name, 'from', dict[p.id]._isReady, 'to', currentPlayer.isReady);
              console.log(`[${new Date().toISOString()}] Player data for ready update:`, currentPlayer);
              const newColor = currentPlayer.isReady ? 0x00ff00 : 0xffffff;
              console.log(`[${new Date().toISOString()}] Setting text color to:`, newColor === 0x00ff00 ? 'GREEN' : 'WHITE');
              dict[p.id].textMesh.material.color.setHex(newColor);
              dict[p.id]._isReady = currentPlayer.isReady;
            }
          }
          console.log(`[${new Date().toISOString()}] Tank already exists for player:`, p.name, 'ID:', p.id);
          return dict[p.id];
        }
      })
    );

    // After all tanks are created, position them
    createTanksPromise.then(() => {
      console.log(`[${new Date().toISOString()}] Positioning tanks, total tanks:`, Object.keys(dict).filter(k => k !== '_lastPlayersKey').length);
      positionOtherTanks();
    });
  }, [players, username]);

  // Handle current user's tank ready status separately (only for custom rooms)
  useEffect(() => {
    if (!isCustomRoom) {
      return; // Skip for public garage
    }

    console.log(`[${new Date().toISOString()}] Current user ready status effect triggered, isReady:`, isReady, 'username:', username);

    if (!myTankRef.current || !username) {
      console.log(`[${new Date().toISOString()}] Early return: myTankRef.current:`, !!myTankRef.current, 'username:', !!username);
      return;
    }

    const myTank = myTankRef.current;
    console.log(`[${new Date().toISOString()}] My tank found:`, !!myTank, 'textMesh:', !!myTank.textMesh);

    if (myTank && myTank.textMesh) {
      const newColor = isReady ? 0x00ff00 : 0xffffff;
      const currentColor = myTank.textMesh.material.color.getHexString();
      console.log(`[${new Date().toISOString()}] Current color: ${currentColor}, New color: ${newColor.toString(16)}, isReady:`, isReady);
      console.log(`[${new Date().toISOString()}] Updating current user tank text color to:`, newColor === 0x00ff00 ? 'GREEN' : 'WHITE');
      myTank.textMesh.material.color.setHex(newColor);
      console.log(`[${new Date().toISOString()}] Color updated to:`, myTank.textMesh.material.color.getHexString());
    } else {
      console.log(`[${new Date().toISOString()}] My tank or text mesh not found`);
    }
  }, [isReady, username, isCustomRoom]);

  // Auto-start game when all players are ready (only for custom rooms with ready system)
  const [autoStartTriggered, setAutoStartTriggered] = useState(false);

  useEffect(() => {
    // Only auto-start if ready system is enabled (custom rooms)
    if (!onReadyToggle || !isReady) {
      return;
    }

    const readyCount = players.filter(p => p.isReady).length;
    const totalCount = players.length;

    if (players.length >= 2 && readyCount === totalCount && onContinue && !autoStartTriggered) {
      console.log(`[${new Date().toISOString()}] All players ready (${readyCount}/${totalCount}), auto-starting game`);
      setAutoStartTriggered(true);
      // Add a small delay to show the ready state
      setTimeout(() => {
        console.log(`[${new Date().toISOString()}] Executing auto-start after delay`);
        onContinue();
      }, 1000);
    }

    // Reset autoStartTriggered when players change (in case someone leaves)
    if (readyCount < totalCount && autoStartTriggered) {
      console.log(`[${new Date().toISOString()}] Resetting auto-start trigger, not all players ready`);
      setAutoStartTriggered(false);
    }
  }, [players, onContinue, autoStartTriggered, onReadyToggle, isReady]);

  // Rozmístění nepřátelských tanků v závislosti na jejich počtu
  const positionOtherTanks = () => {
    const dict = otherTanksRef.current;
    const enemyTanks = Object.values(dict).filter(tank => tank && tank.object); // Filter out non-tank entries
    const n = enemyTanks.length;
    const center = new THREE.Vector3(0, 0.6, 0);
    console.log(`[${new Date().toISOString()}] Positioning ${n} enemy tanks`);
    if (n === 0) return;
    if (n === 1) {
      // Jeden nepřítel umístíme přímo před můj tank (směr záporné Z)
      const radius = 8;
      enemyTanks[0].object.position.set(0, 0.6, -radius);
      enemyTanks[0].object.lookAt(center);
      enemyTanks[0].object.rotation.y += Math.PI; // otočení o 180°
      console.log(`[${new Date().toISOString()}] Positioned 1 tank at (0, 0.6, -${radius})`);
    } else {
      // Více nepřátel rozmístíme do kruhu s počátečním úhlem tak, aby jeden byl vpředu.
      const radius = 6 + n * 2;
      const startAngle = -Math.PI / 2;
      const angleStep = (2 * Math.PI) / n;
      for (let i = 0; i < n; i++) {
        const angle = startAngle + i * angleStep;
        const x = radius * Math.cos(angle);
        const z = radius * Math.sin(angle);
        enemyTanks[i].object.position.set(x, 0.6, z);
        enemyTanks[i].object.lookAt(center);
        enemyTanks[i].object.rotation.y += Math.PI;
        console.log(`[${new Date().toISOString()}] Positioned tank ${i + 1} at (${x.toFixed(2)}, 0.6, ${z.toFixed(2)})`);
      }
    }
  };

  const SkinSelectorComponent = SkinSelectorProp;
  const togglePanel = () => setPanelOpen(prev => !prev);
  const collapsedLabel = isCustomRoom
    ? `Garáž · ${readyPlayersCount}/${totalPlayersCount} připraveno`
    : `Garáž · ${players.length} hráčů`;

  return (
    <div ref={containerRef} className={`game-preview ${isMobile ? 'is-mobile' : ''}`}>
      <div className="canvas-overlay">
        {isMobile && (
          <button
            type="button"
            className={`garage-panel-toggle ${panelOpen ? 'open' : ''}`}
            onClick={togglePanel}
          >
            {panelOpen ? 'Zavřít panel' : collapsedLabel}
          </button>
        )}
        <div className={`garage-right-panel ${isMobile ? 'mobile' : 'desktop'} ${panelOpen ? 'open' : ''}`}>
          {isMobile && (
            <button
              type="button"
              className="garage-panel-close"
              onClick={() => setPanelOpen(false)}
              aria-label="Zavřít panel"
            >
              ×
            </button>
          )}
          <div className="garage-panel-scroll">
            {onSelectSkin && SkinSelectorComponent && (
              <div className="skin-selector-container">
                <SkinSelectorComponent selectedSkin={selectedSkin} onSelectSkin={onSelectSkin} />
              </div>
            )}

            {isCustomRoom && (
              <button
                className={`ready-button ${isReady ? 'ready' : 'not-ready'}`}
                onClick={() => {
                  console.log('Ready button clicked, current isReady:', isReady);
                  onReadyToggle();
                }}
                title={isReady ? 'Zrušit připravenost' : 'Připraven'}
              >
                {isReady ? 'Zrušit připravenost' : 'Připraven'}
              </button>
            )}

            {players && players.length > 0 && (
              <div className="panel-section">
                <div className="panel-section-header">
                  <h3>Hráči v garáži</h3>
                  {isCustomRoom && (
                    <span className="panel-badge">{readyPlayersCount}/{totalPlayersCount} připraveno</span>
                  )}
                </div>
                <ul className="players-list">
                  {players.map((p) => (
                    <li key={p.id} className="player-item">
                      <img src={`/assets/${p.color || 'T90A'}.png`} alt={p.color} className="player-skin-thumb" />
                      <span className="player-name">{p.name || 'Neznámý'}</span>
                      {isCustomRoom && (
                        <span className={`player-ready-flag ${p.isReady ? 'ready' : 'waiting'}`}>
                          {p.isReady ? '✓ Připraven' : '• Čeká'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>

          {(onLeave || onContinue) && (
            <div className={`garage-actions ${isMobile ? 'mobile' : ''}`}>
              {onLeave && (
                <button
                  className="leave-button icon-only"
                  onClick={onLeave}
                  title="Odejít z garáže"
                  aria-label="Odejít z garáže"
                >
                  <span aria-hidden="true">X</span>
                </button>
              )}
              {onContinue && (
                <button
                  className="play-game-button"
                  onClick={onContinue}
                  title="Spustit hru"
                >
                  Hrát
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {isMobile && !isLandscape && (
        <div className="orientation-lock">
          <div className="orientation-card">
            <div className="orientation-icon" aria-hidden="true">📱</div>
            <h2>Otočte zařízení</h2>
            <p>Pro nejlepší zážitek používejte garáž v režimu na šířku.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GaragePreview;





